import { useState } from 'react'

import { Box, Icon, Tag, Text, Tooltip } from '@factorialco/deprecated-design-system'
import { F0Button } from '@factorialco/f0-react'
import {
  JoinPremiumOfferPricingFragment,
  JoinPremiumProductFragment,
} from 'generated/resources/graphql'

import i18n from 'modules/core/lib/i18n'

import { PremiumOfferAvatar } from 'modules/ats/pages/JobPostingMarketplace/PremiumOfferAvatar'
import { PremiumOfferBookedBadge } from 'modules/ats/pages/JobPostingMarketplace/PremiumOfferBookedBadge'
import { PremiumOfferPurchaseModal } from 'modules/ats/pages/JobPostingMarketplace/PremiumOfferPurchaseModal'
import { premiumOfferCardStyle } from 'modules/ats/pages/JobPostingMarketplace/index.vanilla.css'
import { usePricingUtils } from 'modules/ats/pages/JobPostingMarketplace/usePricingUtils'

interface SubProductPricing {
  name: string
  pricing: JoinPremiumOfferPricingFragment | undefined
}

interface Props {
  product: JoinPremiumProductFragment
}

export const PremiumProductCard = ({ product }: Props) => {
  const [infoTooltipOpen, setInfoTooltipOpen] = useState<string | null>(null)
  const [purchaseModalOpenId, setPurchaseModalOpenId] = useState<string | null>(null)
  const { formatPrice } = usePricingUtils()

  const productPricing: SubProductPricing[] = product.products.map((p) => ({
    name: p.name,
    pricing: p.pricing,
  }))

  const getProductPercentageDiscount = (productName: string) => {
    const discount = productPricing.find((p) => p.name === productName)?.pricing?.reducedPercentage

    return !!discount && discount
  }

  const getProductPrice = (productName: string) => {
    const pricing = productPricing.find((p) => p.name === productName)?.pricing

    if (!pricing) return null
    const price = pricing.reducedPercentage ? pricing.reducedAmount : pricing.originalAmount

    return formatPrice(price, pricing.currencyCode)
  }

  if (productPricing.some((p) => !p.pricing)) return null

  return (
    <Box className={premiumOfferCardStyle}>
      <Box padding="s16" gap="s8" flexDirection="row" alignItems="center">
        <PremiumOfferAvatar
          identifier={product.slug}
          tooltipContent={
            <>
              <Text.Normal size="200" weight="semibold">
                {product.name}
              </Text.Normal>
              <Text.Normal size="200">{product.description}</Text.Normal>
            </>
          }
          logoUrl={product.logoUrl}
        />
        <Text.Normal color="grey1000" size="300" weight="semibold">
          {product.name}
        </Text.Normal>
      </Box>
      {product.products.map((subProduct) => (
        <Box
          key={`premium-subproduct-${subProduct.id}`}
          border={{
            top: {
              color: 'grey400',
              width: 's1',
              style: 'solid',
            },
          }}
          padding="s16"
          gap="s12"
          flexDirection="row"
          justifyContent="spaceBetween"
          alignItems="center"
        >
          <Box flexDirection="row" gap="s8" alignItems="center">
            <Box flexDirection="row" gap="s4" alignItems="center">
              <Text.Normal color="grey1000">{subProduct.name}</Text.Normal>
              <Box
                onMouseEnter={() => setInfoTooltipOpen(subProduct.name)}
                onMouseLeave={() => setInfoTooltipOpen(null)}
              >
                <Tooltip.Alt
                  attachment="top left"
                  textAlign="left"
                  size="big"
                  open={infoTooltipOpen === subProduct.name}
                  label={subProduct.description}
                >
                  <Icon.Small icon="info-circle" />
                </Tooltip.Alt>
              </Box>
            </Box>

            <Tag inline type="malibu" size="small">
              {i18n.t('ats.job_details.marketplace.common.duration_days', {
                duration: subProduct.durationDays,
              })}
            </Tag>
            {getProductPercentageDiscount(subProduct.name) && (
              <Tag inline leftItem="circle" type="correct" size="small">
                -{getProductPercentageDiscount(subProduct.name)}%
              </Tag>
            )}
          </Box>
          {subProduct.isBooked ? (
            <PremiumOfferBookedBadge />
          ) : (
            <F0Button
              variant="outline"
              label={getProductPrice(subProduct.name)}
              onClick={() => setPurchaseModalOpenId(subProduct.id)}
              disabled={subProduct.isBooked}
            />
          )}

          {purchaseModalOpenId === subProduct.id && (
            <PremiumOfferPurchaseModal
              premiumProduct={product}
              offerName={product.name}
              offerId={subProduct.id}
              offerType={'product'}
              offerProductName={subProduct.name}
              pricing={subProduct.pricing}
              isOpen={purchaseModalOpenId === subProduct.id}
              onClose={() => setPurchaseModalOpenId(null)}
            />
          )}
        </Box>
      ))}
    </Box>
  )
}
